import { Link } from "react-router-dom";
import { Quote } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { TESTIMONIALS } from "@/data/content";
import Reveal from "@/components/Reveal";
import GoldCorners from "@/components/GoldCorners";

const HEADINGS = {
  en: { eyebrow: "Testimonials", title: "Voices from a private practice.", lead: "Carol's clients rarely allow their names in public. The words below are shared with permission, edited for discretion." },
  pt: { eyebrow: "Depoimentos", title: "Vozes de uma prática privada.", lead: "Os clientes da Carol raramente permitem seus nomes em público. As palavras abaixo são compartilhadas com permissão, editadas por discrição." },
  ar: { eyebrow: "آراء العملاء", title: "أصوات من ممارسة خاصة.", lead: "نادراً ما يسمح عملاء كارول بنشر أسمائهم. الكلمات أدناه تُشارك بإذن منهم، وقد حُرّرت حفاظاً على الخصوصية." },
};

export default function Testimonials() {
  const { lang, t: tt } = useLang();
  const h = HEADINGS[lang] || HEADINGS.en;

  return (
    <div data-testid="page-testimonials">
      <section className="bg-[#070E13] text-[#F4EEE8] pt-40 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 motif-grid opacity-30" />
        <div className="absolute inset-0 motif-hex opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#070E13]/40 to-[#070E13]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <p className="overline gold-text">{h.eyebrow}</p>
            <h1 className="h-editorial text-5xl sm:text-6xl lg:text-7xl mt-6 max-w-4xl">{h.title}</h1>
            <p className="mt-8 max-w-2xl text-lg text-[#D6D1C9]">{h.lead}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#070E13] py-20 sm:py-28 border-t border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal
                key={t.id}
                delay={(i % 2) * 80}
                data-testid={`testimonial-${i}`}
                className="relative bg-[#0C1821] text-[#F4EEE8] p-8 lg:p-10 border border-[#C7A26C]/20 hover:border-[#C7A26C]/55 transition-colors flex flex-col"
              >
                <GoldCorners size={12} inset={-1} />
                <Quote className="text-[#C7A26C] mb-6" size={28} strokeWidth={1.5} />
                <p className="font-serif text-lg sm:text-xl leading-relaxed text-[#F4EEE8]/90 italic">
                  “{t.quote[lang] || t.quote.en}”
                </p>
                <div className="mt-8 pt-6 border-t border-[#C7A26C]/20 flex items-center gap-4">
                  <img
                    src={t.photo}
                    alt={t.name}
                    className="w-14 h-14 rounded-full object-cover border border-[#C7A26C]/40 grayscale"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-serif text-lg text-[#F4EEE8]">{t.name}</p>
                    <p className="text-xs text-[#C7A26C] tracking-widest uppercase mt-1">{t.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#070E13] text-[#F4EEE8] py-24 border-t border-[#C7A26C]/15">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <p className="font-serif italic text-3xl sm:text-4xl leading-snug">
              “{tt.testimonialsExtra.closingQuote}”
            </p>
            <Link
              to="/contact"
              data-testid="testimonials-cta"
              className="cta-premium inline-flex items-center gap-2 mt-12 px-9 py-4 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] text-xs uppercase tracking-[0.24em] font-medium"
            >
              {tt.testimonialsExtra.cta}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
